'use client';

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Box, HStack, type BoxProps } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowUp01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Button } from '../../../components/button';
import { useChatExpansionState } from '../../chat/expansion/chat-expansion-state';

type CliClampedBlockProps = Omit<BoxProps, 'children'> & {
  children: ReactNode;
  collapsedMaxHeightPx: number;
  expansionKey?: string;
};

export function CliClampedBlock({
  children,
  collapsedMaxHeightPx,
  expansionKey,
  ...boxProps
}: CliClampedBlockProps) {
  const tChat = useTranslations('designSystem.chat');
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useChatExpansionState({
    expansionKey,
    defaultExpanded: false,
  });

  useLayoutEffect(() => {
    const content = contentRef.current;

    if (!content) {
      return;
    }

    const measure = () =>
      setIsOverflowing(content.scrollHeight > collapsedMaxHeightPx + 1);
    const observer = new ResizeObserver(measure);

    measure();
    observer.observe(content);

    return () => observer.disconnect();
  }, [collapsedMaxHeightPx]);

  const isClamped = isOverflowing && !isExpanded;

  return (
    <Box {...boxProps}>
      <Box
        ref={contentRef}
        maxH={isClamped ? `${collapsedMaxHeightPx}px` : undefined}
        overflow="hidden"
      >
        {children}
      </Box>
      {isOverflowing ? (
        <HStack justify="flex-start" pt={1}>
          <Button
            variant="ghost"
            size="2xs"
            fontFamily="body"
            onClick={(event) => {
              event.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? (
              <ArrowUp01Icon size={12} />
            ) : (
              <ArrowDown01Icon size={12} />
            )}
            {isExpanded ? tChat('showLess') : tChat('showMore')}
          </Button>
        </HStack>
      ) : null}
    </Box>
  );
}
